<template>
  <div :class="rootClass">
    <div
      v-if="!isMine"
      class="task-comment__avatar"
      :class="{ 'task-comment__avatar--placeholder': !showAuthorMeta }"
      :style="avatarStyle"
      :title="authorFullName"
    >{{ initials }}</div>

    <div class="task-comment__body">
      <div v-if="!isMine && showAuthorMeta" class="task-comment__author">
        <span>{{ authorFullName }}</span>
      </div>

      <div v-if="isEditing" class="task-comment__edit-form">
        <div class="task-comment__edit-field">
          <textarea
            ref="editArea"
            v-model="editText"
            :rows="editRows"
            @input="autoresizeEdit"
            @keydown="onEditKeydown"
          />
        </div>
        <div class="actions">
          <button type="button" class="btn btn-sm btn-light" @click="cancelEdit">
            <i class="fas fa-times mr-1" />{{ $t('Cancel') }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-primary"
            :disabled="!canSaveEdit"
            @click="saveEdit"
          >
            <i class="fas fa-check mr-1" />{{ $t('Save') }}
          </button>
        </div>
      </div>

      <div v-else class="task-comment__bubble">
        <p class="task-comment__text">{{ comment.text }}</p>
        <div class="task-comment__meta">
          <i v-if="isSending" class="fas fa-clock status-icon" :title="$t('Sending…')" />
          <span v-if="isError" class="error-label">
            <button type="button" class="task-comment__retry" @click="$emit('retry', comment)">
              <i class="fas fa-redo mr-1" />{{ $t('Retry') }}
            </button>
          </span>
          <span v-else>{{ timeLabel }}</span>
          <span v-if="wasEdited" class="edited">{{ $t('(edited)') }}</span>
        </div>
      </div>

      <div
        v-if="canManage && !isEditing && !isSending && !isError"
        class="task-comment__actions"
      >
        <button
          type="button"
          class="task-comment__action-btn"
          :title="$t('Edit comment')"
          @click="startEdit"
        ><i class="fas fa-pen" /></button>
        <button
          type="button"
          class="task-comment__action-btn"
          :title="$t('Delete comment')"
          @click="$emit('delete', comment)"
        ><i class="fas fa-trash-alt" /></button>
      </div>
    </div>
  </div>
</template>

<script>
const PALETTE = [
  '#00ACAC', '#7348D5', '#1e6ac1', '#24A147', '#ff6f00',
  '#DA1E28', '#62B8B8', '#4B92E3', '#90a4ae', '#ffc107'
]

function hashColor (id) {
  if (id === undefined || id === null) return PALETTE[0]
  const n = Number(id) || String(id).split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return PALETTE[Math.abs(n) % PALETTE.length]
}

export default {
  name: 'TaskCommentMessage',
  props: {
    comment: { type: Object, required: true },
    isMine: { type: Boolean, default: false },
    showAuthorMeta: { type: Boolean, default: true },
    isFirstOfGroup: { type: Boolean, default: true },
    canManage: { type: Boolean, default: false },
    authorName: { type: String, default: '' }
  },
  data () {
    return { isEditing: false, editText: '', editRows: 1 }
  },
  computed: {
    isSending () { return this.comment.__status === 'sending' },
    isError () { return this.comment.__status === 'error' },
    rootClass () {
      return [
        'task-comment',
        this.isMine ? 'task-comment--mine' : 'task-comment--other',
        this.isFirstOfGroup ? 'task-comment--first-of-group' : '',
        this.isSending ? 'task-comment--sending' : '',
        this.isError ? 'task-comment--error' : ''
      ]
    },
    authorFullName () {
      if (this.isMine) return this.$t('You')
      return this.authorName || `#${this.comment.author}`
    },
    initials () {
      const name = this.authorFullName
      if (!name || name.startsWith('#')) return '?'
      const parts = name.trim().split(/\s+/)
      const first = parts[0] ? parts[0][0] : ''
      const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (first + last).toUpperCase() || '?'
    },
    avatarStyle () {
      return { background: hashColor(this.comment.author) }
    },
    timeLabel () {
      const d = this.comment.date_create ? new Date(this.comment.date_create) : null
      if (!d || isNaN(d.getTime())) return ''
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return `${hh}:${mm}`
    },
    wasEdited () {
      if (!this.comment.date_create || !this.comment.date_last_modify) return false
      try {
        const a = new Date(this.comment.date_create).getTime()
        const b = new Date(this.comment.date_last_modify).getTime()
        return Math.abs(b - a) > 1000
      } catch (e) {
        return false
      }
    },
    canSaveEdit () {
      const v = (this.editText || '').trim()
      return v.length > 0 && v !== (this.comment.text || '').trim()
    }
  },
  methods: {
    startEdit () {
      this.editText = this.comment.text || ''
      this.isEditing = true
      this.$nextTick(() => {
        const ta = this.$refs.editArea
        if (ta) {
          ta.focus()
          ta.setSelectionRange(ta.value.length, ta.value.length)
          this.autoresizeEdit()
        }
      })
    },
    cancelEdit () {
      this.isEditing = false
      this.editText = ''
    },
    saveEdit () {
      if (!this.canSaveEdit) return
      this.$emit('edit', { id: this.comment.id, text: this.editText.trim() })
      this.isEditing = false
    },
    autoresizeEdit () {
      const ta = this.$refs.editArea
      if (!ta) return
      ta.style.height = 'auto'
      ta.style.height = Math.min(ta.scrollHeight, 22 * 6) + 'px'
    },
    onEditKeydown (e) {
      if (e.key === 'Escape') { this.cancelEdit() }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        if (this.canSaveEdit) this.saveEdit()
      }
    }
  }
}
</script>
